import styled from '@emotion/styled';
import {Col, media, Row, Spacer} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {PayloadLexical} from 'UI/components/layout/PayloadLexical';
import {H2, Label} from 'UI/components/layout/Text';
import {ViewportTrigger} from 'UI/components/layout/ViewportTrigger';

import {FadeInAnimation} from 'UI/animations/shared';

import {useIntroHeader} from './useIntroHeader';

import type {TextImageModuleIntro} from 'types/payload-types';

/**
 * The props for the `IntroHeader` component, which renders an introductory section with optional
 * overline, headline, and descriptive text.
 * This interface defines the expected properties needed to configure the component, including an
 * optional block ID for anchor linking, an intro object for content, and a test identifier for
 * automated testing purposes.
 */
interface ComponentProps {
    /**
     * An optional identifier applied as the `id` attribute of the headline.
     * Useful for in-page navigation and accessibility (e.g., anchor links).
     */
    blockId?: string | null;
    /**
     * The intro content configuration, which may contain an overline, headline,
     * and descriptive text. If omitted or `null`, nothing will be rendered.
     */
    intro?: TextImageModuleIntro | null;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'IntroHeader'
     */
    testId?: string;
}

/**
 * A section header component that renders an introduction with optional overline, headline, and text.
 * This component uses the `useIntroHeader` hook to extract and normalize header content from the `intro`
 * configuration. It conditionally renders an overline, a headline, and rich text content. The overline
 * is marked as presentational text for accessibility purposes, while the headline is rendered as an `<h2>`
 * with an optional `id` attribute for anchor linking. If a headline is present, the wrapper row is marked
 * up as a `<header>` element.
 * The component also includes spacing between elements to maintain a consistent layout. If neither
 * a headline nor text is provided, the component renders `null`.
 *
 * @param props         The component props.
 * @param props.blockId An optional identifier applied as the `id` attribute of the headline for in-page navigation and accessibility.
 * @param props.intro   The intro content configuration containing overline, headline, and text.
 * @param props.testId  An optional identifier used for testing, applied as a `data-cy` attribute. Defaults to `'IntroHeader'`.
 * @returns A JSX element rendering the introduction header section, or `null` if no content is provided.
 *
 * @example
 * ```tsx
 * <IntroHeader
 *   blockId="about-section"
 *   intro={{
 *     overline: 'Welcome',
 *     headline: 'About Us',
 *     text: 'We are a company committed to excellence.'
 *   }}
 * />
 * ```
 */
const IntroHeader = observer(({blockId, intro, testId = 'IntroHeader'}: ComponentProps) => {
    const {headline, overline, text} = useIntroHeader(intro);

    return (overline || headline || text) ? (
        <ViewportTrigger as={headline ? 'header' : undefined} baseComponent={Row} data-cy={testId} justify="center">
            <StyledCol align="center" xs={12}>
                {overline && (
                    <>
                        <MotionLabel
                            aria-hidden="true"
                            role="presentation"
                            variants={FadeInAnimation}
                        >
                            {overline}
                        </MotionLabel>
                        <Spacer y={4} />
                    </>
                )}
                {headline && (
                    <MotionH2
                        $align="center"
                        id={blockId ?? undefined}
                        variants={FadeInAnimation}
                    >
                        {headline}
                    </MotionH2>
                )}
                {text && (
                    <>
                        <Spacer y={10} />
                        <MotionTextWrapper variants={FadeInAnimation}>
                            <PayloadLexical description={text} />
                        </MotionTextWrapper>
                    </>
                )}
                <Spacer y={{
                    xs: 18, lg: 24
                }}
                />
            </StyledCol>
        </ViewportTrigger>
    ) : null;
});

IntroHeader.displayName = 'IntroHeader';

export {IntroHeader};

const StyledCol = styled(Col)`
    ${media('xs')} {
        max-width: 68.4rem;
        width: 100%;

        & > p {
            text-align: center;
        }
    }
`;

const MotionLabel = styled(motion.create(Label))``;

const MotionH2 = styled(motion.create(H2))``;

const MotionTextWrapper = styled(motion.div)``;